// getUserProfileJsonLd.js
import getProfileTdk from './getProfileTdk.js';
import {
  getPerson,
  getFlixVideoObject,
  getFlixPreviewVideoObject,
  getInteractionCounter,
  getStoryVideoObject,
  getStoryPreviewVideoObject,
  getShortSchemaObject,
  getPostImageObject,
} from './SchemaObject.js';
import {
  getPoster,
  getAssetManifest,
  MediaAssetFormat,
  MediaPreset,
} from './getMediaAsset.js';
import { ProfileListType } from './profileConstants.js';
import { EMPTY_ARRAY } from './defaults.js';
import getMessageDataList from '../selector/getMessageDataList.js';
import getListData from '../selector/getListData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import {
  OUTBOX_FEED_STORY,
  OUTBOX_FEED_POST_VIDEO,
  OUTBOX_FEED_POST_IMAGE,
  OUTBOX_FEED_SHORT,
  SUPPORTED_LANGUAGES,
  USER_PROFILE_TABS,
} from '../RemoteConfigKeys.js';
import { getFeedNameType, sanitizeFeedQuery } from './feedUtils.js';
import { getLanguageTranslationKey } from './getCurrentLanguage.js';
import { getI18nInstance } from './i18n.js';
import {
  DEFAULT_LANGUAGE,
  localeConvertor,
  PREFERRED_LOCALES,
} from './i18nCommon.js';
import { TranslationNamespace } from './translationNamespace.js';

/**
 * Generate JSON-LD data for user profile page
 * @param {Object} params
 * @param {Object} params.state - Redux state
 * @param {string} params.userId - User ID
 * @param {string} params.username - Username
 * @param {string} params.biography - User biography
 * @param {string} params.type - Profile list type (story, flix, image, short)
 * @param {Function} params.t - Translation function
 * @returns {Object} JSON-LD data object
 */
export const getUserProfileJsonLd = async ({
  state,
  userId,
  username = '',
  biography = '',
  type = ProfileListType.STORY,
  t,
}) => {
  const { i18n } = await getI18nInstance();
  // Get message data lists for different feed types
  const [storyDataList, flixDataList, imageDataList, shortsDataList] = [
    OUTBOX_FEED_STORY,
    OUTBOX_FEED_POST_VIDEO,
    OUTBOX_FEED_POST_IMAGE,
    OUTBOX_FEED_SHORT,
  ].map(item => {
    let category = getRemoteConfigData(state, item, undefined, {
      USER_ID: userId,
    });
    category = sanitizeFeedQuery({ feedName: category });
    const ids =
      getListData(
        state,
        ['home', getFeedNameType({ feedName: category }), category],
        'itemIds'
      ) || [];
    const messageDataList = getMessageDataList(state, ids);
    return messageDataList;
  });

  const supportedLanguages =
    getRemoteConfigData(state, SUPPORTED_LANGUAGES) || PREFERRED_LOCALES;
  const alternateLanguages = supportedLanguages.filter(
    language => language !== DEFAULT_LANGUAGE
  );

  const loadLanguagePromises = alternateLanguages.map(async lang => {
    if (!i18n.hasResourceBundle(lang, TranslationNamespace.SEO)) {
      await i18n.reloadResources(lang, TranslationNamespace.SEO);
    }
  });
  await Promise.all(loadLanguagePromises);

  const tabs = getRemoteConfigData(state, USER_PROFILE_TABS) || EMPTY_ARRAY;
  const isFirstTabActived = type === tabs[0];

  const localeToBCP47 = lang =>
    localeConvertor({ locale: lang, isBCP47: true });
  const buildNameValueByLang = (lang, translateFn) => ({
    '@value': translateFn(lang),
    '@language': localeToBCP47(lang),
  });

  const getProfileTdkByLang = (lang, listTypeOption = type) => {
    const t = (key, options = {}) => i18n.t(key, { ...options, lng: lang });
    return getProfileTdk({
      username,
      biography,
      listType: listTypeOption,
      t,
    });
  };

  const getProfileTdkNameByLang = (lang, listTypeOption = type) =>
    getProfileTdkByLang(lang, listTypeOption).displayTitle;
  const getProfileTdkNameValueByLang = (lang, listTypeOption = type) =>
    buildNameValueByLang(lang, l => getProfileTdkNameByLang(l, listTypeOption));

  const getProfileTdkDescriptionByLang = (lang, listTypeOption = type) =>
    getProfileTdkByLang(lang, listTypeOption).displayDescription;
  const getProfileTdkDescriptionValueByLang = (lang, listTypeOption = type) =>
    buildNameValueByLang(lang, l =>
      getProfileTdkDescriptionByLang(l, listTypeOption)
    );

  const buildAlternateValues = translateFn =>
    alternateLanguages.map(lang => ({
      '@value': translateFn(lang),
      '@language': localeToBCP47(lang),
    }));

  return {
    '@context': 'https://schema.org',
    '@graph': [
      getPerson({
        id: userId,
        username,
        biography,
        hasOfferCatalog: [
          {
            '@id': `https://swag.live/user/${userId}#stories`,
            url: `https://swag.live/user/${userId}`,
          },
          {
            '@id': `https://swag.live/user/${userId}/flix#flixes`,
            url: `https://swag.live/user/${userId}/flix`,
          },
          {
            '@id': `https://swag.live/user/${userId}/image#images`,
            url: `https://swag.live/user/${userId}/image`,
          },
        ],
      }),
      {
        '@type': 'ProfilePage',
        '@id': `https://swag.live/user/${userId}/${type}#webpage`,
        url: `https://swag.live/user/${userId}/${type}`,
        inLanguage: supportedLanguages.map(lang => ({
          '@type': 'Language',
          name: t(getLanguageTranslationKey({ language: lang }), {
            ns: TranslationNamespace.SEO,
          }),
          alternateName: localeToBCP47(lang),
        })),
        name: getProfileTdkNameValueByLang(DEFAULT_LANGUAGE),
        alternateName: buildAlternateValues(getProfileTdkNameByLang),
        description: getProfileTdkDescriptionValueByLang(DEFAULT_LANGUAGE),
        alternateDescription: buildAlternateValues(
          getProfileTdkDescriptionByLang
        ),
        breadcrumb: {
          '@id': `https://swag.live/user/${userId}/${type}#breadcrumb`,
        },
        mainEntity: {
          '@id': `https://swag.live/user/${userId}#person`,
        },
        significantLinks: tabs.map((tab, index) => {
          if (index === 0) {
            return `https://swag.live/user/${userId}`;
          }
          return `https://swag.live/user/${userId}/${tab}`;
        }),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `https://swag.live/user/${userId}/${type}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: buildNameValueByLang(DEFAULT_LANGUAGE, lang =>
              i18n.t('seo-home-title', {
                ns: TranslationNamespace.SEO,
                lng: lang,
              })
            ),
            alternateName: buildAlternateValues(lang =>
              i18n.t('seo-home-title', {
                ns: TranslationNamespace.SEO,
                lng: lang,
              })
            ),
            item: 'https://swag.live',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: {
              '@value': getProfileTdk({
                username,
                biography,
                listType: tabs[0],
                t: (key, options = {}) =>
                  i18n.t(key, { ...options, lng: DEFAULT_LANGUAGE }),
              }).displayTitle,
              '@language': localeToBCP47(DEFAULT_LANGUAGE),
            },
            alternateName: buildAlternateValues(lang =>
              getProfileTdkNameByLang(lang, tabs[0])
            ),
            item: `https://swag.live/user/${userId}`,
          },
          !isFirstTabActived && {
            '@type': 'ListItem',
            position: 3,
            name: getProfileTdkNameValueByLang(DEFAULT_LANGUAGE),
            alternateName: buildAlternateValues(getProfileTdkNameByLang),
            item: `https://swag.live/user/${userId}/${type}`,
          },
        ].filter(a => a),
      },
      {
        '@type': 'OfferCatalog',
        '@id': `https://swag.live/user/${userId}#person`,
        url: `https://swag.live/user/${userId}`,
        itemListElement: [
          ...storyDataList,
          ...flixDataList,
          ...shortsDataList,
          ...imageDataList,
        ].map((messageData, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@id': `https://swag.live/post/${messageData.id}#preview`,
          },
        })),
      },
      {
        '@type': 'OfferCatalog',
        '@id': `https://swag.live/user/${userId}/${ProfileListType.STORY}#stories`,
        url: `https://swag.live/user/${userId}/${ProfileListType.STORY}`,
        itemListElement: storyDataList.map((messageData, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@id': `https://swag.live/post/${messageData.id}#preview`,
          },
        })),
      },
      {
        '@type': 'OfferCatalog',
        '@id': `https://swag.live/user/${userId}/${ProfileListType.FLIX}#flixes`,
        url: `https://swag.live/user/${userId}/${ProfileListType.FLIX}`,
        itemListElement: flixDataList.map((messageData, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@id': `https://swag.live/post/${messageData.id}#preview`,
          },
        })),
      },
      {
        '@type': 'OfferCatalog',
        '@id': `https://swag.live/user/${userId}/${ProfileListType.SHORT}#shorts`,
        url: `https://swag.live/user/${userId}/${ProfileListType.SHORT}`,
        itemListElement: shortsDataList.map((messageData, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@id': `https://swag.live/post/${messageData.id}#preview`,
          },
        })),
      },
      {
        '@type': 'OfferCatalog',
        '@id': `https://swag.live/user/${userId}/${ProfileListType.IMAGE}#images`,
        url: `https://swag.live/user/${userId}/${ProfileListType.IMAGE}`,
        itemListElement: imageDataList.map((messageData, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@id': `https://swag.live/post/${messageData.id}#preview`,
          },
        })),
      },
      ...storyDataList.map(messageData => {
        return getStoryPreviewVideoObject({
          id: messageData.id,
          assetId: messageData?.assetIds?.[0],
          username,
          title: messageData.title,
          caption: messageData.caption,
          durationSecs: messageData.media?.duration,
          postedAtUnix: messageData.postedAtUnix,
          author: {
            '@id': `https://swag.live/user/${userId}#person`,
          },
        });
      }),
      ...storyDataList.map(messageData => {
        return getStoryVideoObject({
          id: messageData.id,
          assetId: messageData?.assetIds?.[0],
          username,
          title: messageData.title,
          caption: messageData.caption,
          durationSecs: messageData.media?.duration,
          postedAtUnix: messageData.postedAtUnix,
          author: {
            '@id': `https://swag.live/user/${userId}#person`,
          },
          isFree: !messageData.unlockPrice,
          interactionStatistic: [
            { type: 'ViewAction', count: messageData.views || 0 },
            { type: 'BuyAction', count: messageData.unlocks || 0 },
            { type: 'LikeAction', count: messageData.likes || 0 },
            {
              type: 'DislikeAction',
              count: messageData.dislikes || 0,
            },
          ]
            .filter(s => s.count)
            .map(getInteractionCounter),
        });
      }),
      ...flixDataList.map(messageData => {
        return getFlixPreviewVideoObject({
          id: messageData.id,
          assetId: messageData?.assetIds?.[0],
          title: messageData.title,
          description: messageData.caption || messageData.title,
          durationSecs: 10,
          postedAtUnix: messageData.postedAtUnix,
          author: {
            '@id': `https://swag.live/user/${userId}#person`,
          },
        });
      }),
      ...flixDataList.map(messageData => {
        return getFlixVideoObject({
          id: messageData.id,
          assetId: messageData?.assetIds?.[0],
          title: messageData.title,
          description: messageData.caption || messageData.title,
          durationSecs: messageData.media?.duration || 600,
          postedAtUnix: messageData.postedAtUnix,
          isFree: !messageData.unlockPrice,
          author: {
            '@id': `https://swag.live/user/${userId}#person`,
          },
          interactionStatistic: [
            { type: 'ViewAction', count: messageData.views || 0 },
            { type: 'BuyAction', count: messageData.unlocks || 0 },
            { type: 'LikeAction', count: messageData.likes || 0 },
            {
              type: 'DislikeAction',
              count: messageData.dislikes || 0,
            },
          ]
            .filter(s => s.count)
            .map(getInteractionCounter),
        });
      }),
      ...shortsDataList.map(messageData => {
        const firstAssetId = messageData?.assetIds?.[0];
        return getShortSchemaObject({
          id: messageData.id,
          title: messageData.title,
          description: messageData.caption || messageData.title,
          durationSecs: messageData.media?.duration,
          author: {
            '@id': `https://swag.live/user/${userId}#person`,
          },
          username,
          postedAtUnix: messageData.postedAtUnix,
          isFree: !messageData.unlockPrice,
          interactionStatistic: [
            { type: 'ViewAction', count: messageData.views || 0 },
            { type: 'BuyAction', count: messageData.unlocks || 0 },
            { type: 'LikeAction', count: messageData.likes || 0 },
            {
              type: 'DislikeAction',
              count: messageData.dislikes || 0,
            },
          ],
          encodings: [
            {
              '@id': `https://swag.live/shorts/${messageData.id}#video-dash`,
            },
          ],
          associatedMedia: messageData.sourceId
            ? {
                '@id': `https://swag.live/post/${messageData.sourceId}#preview`,
              }
            : undefined,
          thumbnailUrl: getPoster({
            id: messageData.id,
            size: '1024x1024',
            format: MediaAssetFormat.JPG,
            preset: MediaPreset.SD,
          }),
          contentUrl: firstAssetId
            ? getAssetManifest({
                id: messageData.id,
                assetId: firstAssetId,
                format: MediaAssetFormat.M3U8,
                preset: MediaPreset.SD,
              })?.href
            : undefined,
        });
      }),
      ...imageDataList.map(messageData => {
        return getPostImageObject({
          id: messageData.id,
          assetId: messageData?.assetIds?.[0],
          description: messageData.caption,
          durationSecs: messageData.media?.duration,
          postedAtUnix: messageData.postedAtUnix,
          isFree: !messageData.unlockPrice,
          author: {
            '@id': `https://swag.live/user/${userId}#person`,
          },
          interactionStatistic: [
            { type: 'ViewAction', count: messageData.views || 0 },
            { type: 'BuyAction', count: messageData.unlocks || 0 },
            { type: 'LikeAction', count: messageData.likes || 0 },
            {
              type: 'DislikeAction',
              count: messageData.dislikes || 0,
            },
          ]
            .filter(s => s.count)
            .map(getInteractionCounter),
        });
      }),
    ],
  };
};

export default getUserProfileJsonLd;
